export const SCENE_TIMEZONE = "America/Los_Angeles";

type DateParts = {
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  second: number
}

function getZonedParts(date: Date, timeZone: string): DateParts {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit"
  }).formatToParts(date).reduce((acc, part) => {
    acc[part.type] = part.value;
    return acc;
  }, {} as Record<string, string>);

  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
    hour: Number(parts.hour),
    minute: Number(parts.minute),
    second: Number(parts.second)
  };
}

/*
 * Offset from UTC, in minutes, for `timeZone` at the given instant
 */
function getTimeZoneOffsetMinutes(date: Date, timeZone: string): number {
  const { year, month, day, hour, minute, second } = getZonedParts(date, timeZone);
  const asUTC = Date.UTC(year, month - 1, day, hour, minute, second);
  return (asUTC - date.getTime()) / 60000;
}

/*
 * Parses a timezone-less "YYYY-MM-DDTHH:mm[:ss]" string as wall-clock time in `timeZone`
 */
export function zonedTimeToUtc(dateTimeLocalValue: string, timeZone: string = SCENE_TIMEZONE): Date {
  const match = dateTimeLocalValue.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/);
  if (!match) {
    return new Date(NaN);
  }

  const [, y, mo, d, h, mi, s] = match;
  const naiveAsUTC = Date.UTC(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi), s ? Number(s) : 0);

  let offsetMinutes = getTimeZoneOffsetMinutes(new Date(naiveAsUTC), timeZone);
  let utcMillis = naiveAsUTC - offsetMinutes * 60000;

  offsetMinutes = getTimeZoneOffsetMinutes(new Date(utcMillis), timeZone);
  utcMillis = naiveAsUTC - offsetMinutes * 60000;

  return new Date(utcMillis);
}

/*
 * Formats an instant as a timezone-less "YYYY-MM-DDTHH:mm" string in `timeZone`, for a datetime-local input
 */
export function formatAsDateTimeLocalValue(date: Date, timeZone: string = SCENE_TIMEZONE): string {
  if (Number.isNaN(date.getTime())) return "";

  const { year, month, day, hour, minute } = getZonedParts(date, timeZone);
  const pad = (n: number) => String(n).padStart(2, "0");

  return `${year}-${pad(month)}-${pad(day)}T${pad(hour)}:${pad(minute)}`;
}
